import { useEffect, useState, useCallback, useRef } from "react";
import { usePortal } from "../PortalShell";
import { portalQueries, readPortalCache, writePortalCache } from "@/client/lib/portal";
import { focusFirstError } from "@/client/lib/focus-error";
import { Button } from "@/client/components/ui/button";
import { Notice } from "@/client/components/ui/notice";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { Checkbox } from "@/client/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPanel,
} from "@/client/components/ui/dialog";
import {
  InfoIcon,
  AlertTriangleIcon,
  AlertCircleIcon,
  CheckCircle2Icon,
  CheckIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
} from "lucide-react";

const YEAR_LEVELS = [
  { code: "groep_1", label: "Leerjaar 1" }, { code: "groep_2", label: "Leerjaar 2" },
  { code: "groep_3", label: "Leerjaar 3" }, { code: "groep_4", label: "Leerjaar 4" },
  { code: "groep_5", label: "Leerjaar 5" }, { code: "groep_6", label: "Leerjaar 6" },
  { code: "groep_7", label: "Leerjaar 7" }, { code: "groep_8", label: "Leerjaar 8" },
];

type ClassGroup = { id: number; groupCode: string; displayName: string; schoolLocationId: number | null; includeInPlanning: boolean; vrijeDagdelenLater: boolean; vrijeDagdelenGeen: boolean; vakdocentLater: boolean; vakdocentGeen: boolean; notes: string | null };

// Per-group availability state for one type, as a single mutually-exclusive choice:
//   "opgeven" — entering concrete moments now;  "geen" — none at all;  "later" — fill in later.
type AvailChoice = "opgeven" | "geen" | "later";
type YearLevel = { classGroupId: number; yearLevelCode: string; studentCount: number };

// ─── Constraint types (mirrors VerhinderingenSection) ───

type Constraint = {
  id: number;
  constraintTypeCode: string;
  constraintEffectCode: string;
  title: string;
  description: string | null;
  appliesToEntireSchool: boolean;
};
type WeeklyPattern = {
  constraintId: number;
  dayOfWeekCode: string;
  startTime: string | null;
  endTime: string | null;
};
type ScopeGroup = { constraintId: number; classGroupId: number };

// Placeholder titles used by the standalone beschikbaarheid steps for "Nee" / "later".
// We never show or save these in the per-group editor.
const ALL_PLACEHOLDERS = [
  "__later__", "__no__",
  "__kleuter_no__", "__kleuter_later__",
  "__groep_no__", "__groep_later__",
  "__vak_no__", "__vak_later__",
];

// Common vakdocenten (subject teachers).
const VAK_OPTIONS = ["Gymdocent", "Zwemmen", "Muziekdocent", "Handvaardigheid", "Anders"];

const WEEKDAYS = [
  { code: "monday", label: "Maandag" },
  { code: "tuesday", label: "Dinsdag" },
  { code: "wednesday", label: "Woensdag" },
  { code: "thursday", label: "Donderdag" },
  { code: "friday", label: "Vrijdag" },
] as const;

type DayPart = "ochtend" | "middag" | "hele_dag";

const DAY_PARTS: { code: DayPart; label: string; startTime: string | null; endTime: string | null }[] = [
  { code: "ochtend", label: "Ochtend", startTime: "08:00", endTime: "12:00" },
  { code: "middag", label: "Middag", startTime: "12:00", endTime: "17:00" },
  { code: "hele_dag", label: "Hele dag", startTime: null, endTime: null },
];

const AUTO_REASON_DAGDEEL = "Vrije dagdeel";

function dayPartFromTimes(startTime: string | null, endTime: string | null): DayPart {
  if (!startTime && !endTime) return "hele_dag";
  if (startTime === "08:00:00" && endTime === "12:00:00") return "ochtend";
  if (startTime === "08:00" && endTime === "12:00") return "ochtend";
  if (startTime === "12:00:00" && endTime === "17:00:00") return "middag";
  if (startTime === "12:00" && endTime === "17:00") return "middag";
  if (startTime && startTime < "12:00") return "ochtend";
  if (startTime) return "middag";
  return "hele_dag";
}

function dayPartTimes(part: DayPart): { startTime: string | undefined; endTime: string | undefined } {
  const dp = DAY_PARTS.find((d) => d.code === part)!;
  return { startTime: dp.startTime ?? undefined, endTime: dp.endTime ?? undefined };
}

// ─── Component ───

export function GroepenSection({ schoolId }: { schoolId: number }) {
  const { primaryAction, setSaveState, setLastSavedAt, saveState } = usePortal();

  // Seed from the prefetched cache for an instant first paint; load() revalidates.
  const cachedClasses = readPortalCache<{ groups?: ClassGroup[]; yearLevels?: YearLevel[]; excludedYearLevels?: string[] }>(portalQueries.classes(schoolId).queryKey);
  const cachedConstraints = readPortalCache<{ constraints?: Constraint[]; weeklyPatterns?: WeeklyPattern[]; scopeGroups?: ScopeGroup[] }>(portalQueries.constraints(schoolId).queryKey);
  const [groups, setGroups] = useState<ClassGroup[]>(() => cachedClasses?.groups ?? []);
  const [yearLevels, setYearLevels] = useState<YearLevel[]>(() => cachedClasses?.yearLevels ?? []);
  const [excludedYearLevels, setExcludedYearLevels] = useState<Set<string>>(() => new Set(cachedClasses?.excludedYearLevels ?? []));
  const [constraints, setConstraints] = useState<Constraint[]>(() => cachedConstraints?.constraints ?? []);
  const [weeklyPatterns, setWeeklyPatterns] = useState<WeeklyPattern[]>(() => cachedConstraints?.weeklyPatterns ?? []);
  const [scopeGroups, setScopeGroups] = useState<ScopeGroup[]>(() => cachedConstraints?.scopeGroups ?? []);
  const [loading, setLoading] = useState(() => !cachedClasses);
  const [error, setError] = useState(false);

  // The group dialog. `dialog` holds the group being edited (null id = creating a new group).
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogGroupId, setDialogGroupId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setError(false);
      const [classesRes, constraintsRes] = await Promise.all([
        fetch(`/api/school/${schoolId}/classes`, { credentials: "include" }),
        fetch(`/api/school/${schoolId}/constraints`, { credentials: "include" }),
      ]);
      if (classesRes.ok && constraintsRes.ok) {
        const classes = await classesRes.json();
        const cons = await constraintsRes.json();
        setGroups(classes.groups ?? []);
        setYearLevels(classes.yearLevels ?? []);
        setExcludedYearLevels(new Set(classes.excludedYearLevels ?? []));
        setConstraints(cons.constraints ?? []);
        setWeeklyPatterns(cons.weeklyPatterns ?? []);
        setScopeGroups(cons.scopeGroups ?? []);
        writePortalCache(portalQueries.classes(schoolId).queryKey, classes);
        writePortalCache(portalQueries.constraints(schoolId).queryKey, cons);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    }
    setLoading(false);
  }, [schoolId]);

  useEffect(() => { load(); }, [load]);

  // Auto-clear excluded year levels that are now covered by a group
  useEffect(() => {
    const covered = new Set(yearLevels.map((y) => y.yearLevelCode));
    const stale = [...excludedYearLevels].filter((code) => covered.has(code));
    if (stale.length > 0) {
      const next = new Set(excludedYearLevels);
      stale.forEach((code) => next.delete(code));
      setExcludedYearLevels(next);
      // Persist the cleanup + keep the shared classes cache in sync so other tabs don't
      // read a stale excluded-list. Best-effort, but we do reconcile from load() on save.
      void fetch(`/api/school/${schoolId}/classes/excluded-year-levels`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ excludedYearLevels: [...next] }),
      }).then((res) => {
        if (res.ok) {
          const cached = readPortalCache<{ excludedYearLevels?: string[] }>(portalQueries.classes(schoolId).queryKey);
          if (cached) writePortalCache(portalQueries.classes(schoolId).queryKey, { ...cached, excludedYearLevels: [...next] });
        }
      }).catch(() => {});
    }
  }, [yearLevels]); // eslint-disable-line react-hooks/exhaustive-deps

  const saved = () => { setSaveState("saved"); setLastSavedAt(new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })); };

  // A duplicate name becomes "Naam (2)", "(3)", ... so two "Groep 6"-en stay distinguishable.
  // `exceptId` excludes the group being renamed so re-saving its own name is a no-op.
  const uniqueGroupName = (base: string, exceptId: number | null): string => {
    const taken = new Set(groups.filter((g) => g.id !== exceptId).map((g) => g.displayName.trim().toLowerCase()));
    if (!taken.has(base.toLowerCase())) return base;
    let n = 2;
    while (taken.has(`${base} (${n})`.toLowerCase())) n++;
    return `${base} (${n})`;
  };
  const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const toggleExcludedYearLevel = async (code: string, checked: boolean) => {
    const next = new Set(excludedYearLevels);
    if (checked) next.add(code); else next.delete(code);
    setExcludedYearLevels(next);
    setSaveState("saving");
    try {
      const res = await fetch(`/api/school/${schoolId}/classes/excluded-year-levels`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ excludedYearLevels: [...next] }),
      });
      if (res.ok) saved(); else setSaveState("error");
    } catch { setSaveState("error"); }
  };

  const openCreate = () => { setDialogGroupId(null); setDialogOpen(true); };
  const openEdit = (id: number) => { setDialogGroupId(id); setDialogOpen(true); };

  if (loading) return <p className="text-sm text-muted-foreground">Laden...</p>;

  if (error) {
    return (
      <Notice
        tone="error"
        icon={<AlertCircleIcon />}
        title="Kan de groepen niet laden"
        action={
          <Button size="sm" variant="outline" onClick={() => { setLoading(true); load(); }}>
            Opnieuw proberen
          </Button>
        }
      >
        Controleer uw internetverbinding en probeer het opnieuw.
      </Notice>
    );
  }

  // Empty-state: no groups yet.
  if (groups.length === 0) {
    return (
      <div>
        <p className="text-sm leading-6 text-muted-foreground">
          Voeg de groepen van uw school toe. Geef per groep aan welke leerjaren erin zitten en hoeveel leerlingen het zijn.
        </p>
        <Button className="mt-4" onClick={openCreate}>
          <PlusIcon aria-hidden="true" className="-ms-1 opacity-60" size={16} />
          Groep toevoegen
        </Button>

        <GroupDialog
          key={dialogOpen ? `${dialogGroupId ?? "new"}` : "closed"}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          schoolId={schoolId}
          groupId={dialogGroupId}
          groups={groups}
          yearLevels={yearLevels}
          constraints={constraints}
          weeklyPatterns={weeklyPatterns}
          scopeGroups={scopeGroups}
          uniqueGroupName={uniqueGroupName}
          slug={slug}
          onSaved={async () => { await load(); saved(); }}
          onError={() => setSaveState("error")}
          onSaving={() => setSaveState("saving")}
        />
      </div>
    );
  }

  // ─── Group list view ───
  const coveredLevels = new Set(yearLevels.map((y) => y.yearLevelCode));
  const missingLevels = YEAR_LEVELS.filter((y) => !coveredLevels.has(y.code));
  const unresolvedLevels = missingLevels.filter((y) => !excludedYearLevels.has(y.code));
  const totalStudents = yearLevels.reduce((s, y) => s + y.studentCount, 0);
  const hasIncompleteGroups = groups.some((g) => {
    const gyl = yearLevels.filter((y) => y.classGroupId === g.id);
    return gyl.length === 0 || gyl.some((y) => y.studentCount === 0);
  });
  // Order the list like the programma sub-nav: by lowest leerjaar, then a single-leerjaar groep
  // before the combigroepen that start with it (Groep 3 -> Combi 3/4 -> Groep 4 -> Combi 4/5/6 ...),
  // then alphabetically/numerically by name. Groups without a leerjaar go last.
  const yearRank = (g: ClassGroup) => {
    const yls = yearLevels.filter((y) => y.classGroupId === g.id);
    if (!yls.length) return 99;
    return Math.min(...yls.map((y) => parseInt(y.yearLevelCode.replace("groep_", ""), 10) || 99));
  };
  const ylCount = (g: ClassGroup) => yearLevels.filter((y) => y.classGroupId === g.id).length;
  const sortedGroups = [...groups].sort((a, b) => yearRank(a) - yearRank(b) || ylCount(a) - ylCount(b) || a.displayName.localeCompare(b.displayName, "nl", { numeric: true }));

  // Count the per-group beschikbaarheid items, split by type, for the card meta lines.
  const groupMomentCounts = (gid: number) => {
    const ids = new Set(scopeGroups.filter((sg) => sg.classGroupId === gid).map((sg) => sg.constraintId));
    const countOf = (type: string) =>
      constraints.filter(
        (c) =>
          ids.has(c.id) &&
          c.constraintTypeCode === type &&
          !c.appliesToEntireSchool &&
          !ALL_PLACEHOLDERS.includes(c.title),
      ).length;
    return { vrijeDagdelen: countOf("recurring_unavailable"), vakdocenten: countOf("vakdocent") };
  };

  // Leerjaar numbers (ascending) + the per-leerjaar student counts in that same order.
  const groupLevelLines = (gid: number) => {
    const sorted = yearLevels
      .filter((y) => y.classGroupId === gid)
      .map((y) => ({ num: parseInt(y.yearLevelCode.replace("groep_", ""), 10) || 0, count: y.studentCount }))
      .sort((a, b) => a.num - b.num);
    return {
      leerjaren: sorted.map((y) => y.num).join(", "),
      aantallen: sorted.map((y) => y.count).join(", "),
    };
  };

  return (
    <div>
      <p className="text-sm leading-6 text-muted-foreground">
        Voeg alle groepen toe. Geef per groep aan welke leerjaren erin zitten, hoeveel leerlingen het zijn, en wanneer de groep vrij is.
      </p>

      {/* Group list */}
      <div className="mt-4 overflow-hidden rounded-lg border shadow-xs">
        {sortedGroups.map((g, i) => {
          const gyl = yearLevels.filter((y) => y.classGroupId === g.id);
          const incomplete = gyl.length === 0 || gyl.some((y) => y.studentCount === 0);
          const counts = groupMomentCounts(g.id);
          const levels = groupLevelLines(g.id);
          return (
            <button
              type="button"
              key={g.id}
              className={`flex w-full cursor-pointer items-start justify-between gap-4 px-4 py-3 text-left hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${i > 0 ? "border-t border-border" : ""}`}
              onClick={() => openEdit(g.id)}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{g.displayName}</p>
                <dl className="mt-1.5 space-y-0.5 text-sm text-muted-foreground">
                  <div className="flex gap-1.5">
                    <dt className="shrink-0">Leerjaren:</dt>
                    <dd className="min-w-0 text-foreground">{levels.leerjaren || "—"}</dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="shrink-0">Leerlingaantallen:</dt>
                    <dd className="min-w-0 text-foreground">{levels.aantallen || "—"}</dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="shrink-0">Vrije dagdelen:</dt>
                    <dd className="min-w-0 text-foreground">
                      {g.vrijeDagdelenLater
                        ? <span className="text-amber-700">wordt later ingevuld</span>
                        : g.vrijeDagdelenGeen
                          ? <span className="text-muted-foreground">Geen</span>
                          : counts.vrijeDagdelen}
                    </dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="shrink-0">Vakdocenten:</dt>
                    <dd className="min-w-0 text-foreground">
                      {g.vakdocentLater
                        ? <span className="text-amber-700">wordt later ingevuld</span>
                        : g.vakdocentGeen
                          ? <span className="text-muted-foreground">Geen</span>
                          : counts.vakdocenten}
                    </dd>
                  </div>
                </dl>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {incomplete && (
                  <span className="text-xs text-amber-600">Incompleet</span>
                )}
                <span className="text-sm font-medium text-primary">Bewerken</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Add group */}
      <Button className="mt-4" onClick={openCreate}>
        <PlusIcon aria-hidden="true" className="-ms-1 opacity-60" size={16} />
        Groep toevoegen
      </Button>

      {/* Incomplete groups warning */}
      {hasIncompleteGroups && (
        <div className="mt-5">
          <Notice tone="warning" icon={<AlertTriangleIcon />} title="Niet alle groepen zijn compleet">
            Vul per groep minimaal een leerjaar en het aantal leerlingen in voordat u doorgaat.
          </Notice>
        </div>
      )}

      {/* Uncovered year levels — per-leerjaar checkboxes */}
      {missingLevels.length > 0 && (
        <div className="mt-5">
          <Notice
            tone="info"
            icon={unresolvedLevels.length > 0 ? <InfoIcon /> : <CheckCircle2Icon />}
            title={unresolvedLevels.length > 0 ? "Nog niet alle leerjaren ingevuld" : "Alle leerjaren ingevuld"}
          >
            <p>
              {unresolvedLevels.length > 0
                ? "Voor deze leerjaren is nog geen groep. Voeg een groep toe, of vink aan dat uw school dat leerjaar niet heeft."
                : "U kunt verder."
              }
            </p>
            <div className="mt-3 space-y-2">
              {missingLevels.map((level) => {
                const isExcluded = excludedYearLevels.has(level.code);
                return (
                  <div key={level.code} className={`flex items-center gap-3 rounded-md border px-3 py-2.5 ${isExcluded ? "border-border/50 bg-muted/50" : "bg-background"}`}>
                    <Checkbox
                      id={`exclude-${level.code}`}
                      checked={isExcluded}
                      onCheckedChange={(checked) => toggleExcludedYearLevel(level.code, !!checked)}
                    />
                    <Label htmlFor={`exclude-${level.code}`} className={`cursor-pointer text-sm ${isExcluded ? "text-muted-foreground/70" : "text-muted-foreground"}`}>
                      Wij hebben dit schooljaar geen leerlingen in {level.label.toLowerCase()}.
                    </Label>
                  </div>
                );
              })}
            </div>
          </Notice>
        </div>
      )}

      {/* Summary + continue */}
      <div className="mt-6 pt-5">
        {groups.length > 0 && totalStudents > 0 && (
          <p className="mb-4 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{groups.length} groep{groups.length !== 1 ? "en" : ""}</span>
            {" · "}{totalStudents} leerlingen totaal
          </p>
        )}
        {unresolvedLevels.length > 0 && !hasIncompleteGroups && groups.length > 0 && (
          <p className="mb-3 text-sm text-amber-600">
            Controleer {unresolvedLevels.length === 1 ? `${unresolvedLevels[0]!.label.toLowerCase()}` : `${unresolvedLevels.length} leerjaren`} voordat u doorgaat.
          </p>
        )}
        <Button
          size="lg"
          onClick={primaryAction.onClick}
          disabled={primaryAction.isConfirm && (groups.length === 0 || hasIncompleteGroups || unresolvedLevels.length > 0 || saveState === "saving")}
        >
          {primaryAction.label}
        </Button>
      </div>

      <GroupDialog
        key={dialogOpen ? `${dialogGroupId ?? "new"}` : "closed"}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        schoolId={schoolId}
        groupId={dialogGroupId}
        groups={groups}
        yearLevels={yearLevels}
        constraints={constraints}
        weeklyPatterns={weeklyPatterns}
        scopeGroups={scopeGroups}
        uniqueGroupName={uniqueGroupName}
        slug={slug}
        onSaved={async () => { await load(); saved(); }}
        onError={() => setSaveState("error")}
        onSaving={() => setSaveState("saving")}
      />
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// GroupDialog — full create/edit form in one dialog
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// One draft moment (vrij dagdeel or vakdocent) in the dialog. `id` is the existing
// constraint id when editing one already saved; null for a new, not-yet-saved row.
type MomentDraft = {
  localId: string;
  id: number | null;
  day: string;
  part: DayPart;
  reason: string; // vakdocent only; vrij dagdelen use AUTO_REASON_DAGDEEL
};

function GroupDialog({
  open, onClose, schoolId, groupId, groups, yearLevels, constraints, weeklyPatterns, scopeGroups,
  uniqueGroupName, slug, onSaved, onError, onSaving,
}: {
  open: boolean;
  onClose: () => void;
  schoolId: number;
  groupId: number | null;
  groups: ClassGroup[];
  yearLevels: YearLevel[];
  constraints: Constraint[];
  weeklyPatterns: WeeklyPattern[];
  scopeGroups: ScopeGroup[];
  uniqueGroupName: (base: string, exceptId: number | null) => string;
  slug: (s: string) => string;
  onSaved: () => Promise<void>;
  onError: () => void;
  onSaving: () => void;
}) {
  const existing = groupId != null ? groups.find((g) => g.id === groupId) ?? null : null;
  const idRef = useRef(0);
  const newLocalId = () => `r${idRef.current++}`;

  // The dialog body; on a failed save we focus + scroll to the first invalid field within it.
  const bodyRef = useRef<HTMLDivElement>(null);

  // ── Form state (seeded once at mount; the keyed remount resets it per open) ──
  const [name, setName] = useState(() => existing?.displayName ?? "");

  const [rows, setRows] = useState<{ localId: string; yearLevelCode: string; studentCount: string }[]>(() =>
    yearLevels
      .filter((y) => y.classGroupId === groupId)
      .map((y) => ({ localId: newLocalId(), yearLevelCode: y.yearLevelCode, studentCount: String(y.studentCount) })),
  );

  // Per-group constraints for THIS group, split by type. We track existing ones so we can
  // diff on save (update kept, delete removed, create added).
  const groupConstraintIds = new Set(scopeGroups.filter((sg) => sg.classGroupId === groupId).map((sg) => sg.constraintId));
  const isGroupMoment = (c: Constraint, type: string) =>
    groupConstraintIds.has(c.id) && c.constraintTypeCode === type && !c.appliesToEntireSchool && !ALL_PLACEHOLDERS.includes(c.title);
  const seedMoments = (type: string, isVak: boolean): MomentDraft[] =>
    constraints
      .filter((c) => isGroupMoment(c, type))
      .map((c) => {
        const wp = weeklyPatterns.find((w) => w.constraintId === c.id);
        return {
          localId: newLocalId(),
          id: c.id,
          day: wp?.dayOfWeekCode ?? "",
          part: wp ? dayPartFromTimes(wp.startTime, wp.endTime) : "ochtend",
          reason: isVak ? c.title : AUTO_REASON_DAGDEEL,
        } as MomentDraft;
      });

  const [dagdelen, setDagdelen] = useState<MomentDraft[]>(() => seedMoments("recurring_unavailable", false));
  const [vakdocenten, setVakdocenten] = useState<MomentDraft[]>(() => seedMoments("vakdocent", true));

  // Each per-group beschikbaarheid type is a single 3-way choice (mutually exclusive):
  //   "opgeven" — the school is entering concrete moments now (the MomentList editor is shown).
  //   "geen"    — this group has none of that type at all (answered, no entries).
  //   "later"   — the school will fill it in later (answered, pending; KUVO keeps it open).
  // Seeded from the group's two booleans: _geen wins over _later (it never stores both), and
  // when both are false the group is "opgeven" (it either has entries or is still answering by
  // adding them). Picking "geen"/"later" hides + clears that type's editor on save; "opgeven"
  // clears both flags and shows the editor.
  const seedChoice = (later: boolean | undefined, geen: boolean | undefined): AvailChoice =>
    geen ? "geen" : later ? "later" : "opgeven";
  const [dagdeelChoice, setDagdeelChoice] = useState<AvailChoice>(() => seedChoice(existing?.vrijeDagdelenLater, existing?.vrijeDagdelenGeen));
  const [vakChoice, setVakChoice] = useState<AvailChoice>(() => seedChoice(existing?.vakdocentLater, existing?.vakdocentGeen));

  // The constraint ids this group started with, so on save we can delete the ones the
  // user removed (a moment whose id is no longer in the draft list).
  const initialDagdeelIds = useRef<number[]>(seedMoments("recurring_unavailable", false).map((m) => m.id!).filter(Boolean));
  const initialVakIds = useRef<number[]>(seedMoments("vakdocent", true).map((m) => m.id!).filter(Boolean));

  const [submitting, setSubmitting] = useState(false);
  // Validation errors stay hidden until the first failed save attempt. The dialog is keyed
  // per open, so this resets to false on every fresh "Groep toevoegen" / edit open.
  const [submitted, setSubmitted] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteDeps, setDeleteDeps] = useState<{ vrijeDagdelen: number; vakdocenten: number; programmaKeuzes: number } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // ── Year-level row helpers ──
  const addRow = () => {
    // New rows start unassigned ("Leerjaar toewijzen") so the user must consciously pick the
    // right leerjaar per entry instead of accepting a defaulted one.
    setRows((p) => [...p, { localId: newLocalId(), yearLevelCode: "", studentCount: "" }]);
  };
  const updateRow = (i: number, field: "yearLevelCode" | "studentCount", value: string) =>
    setRows((p) => p.map((r, j) => (j === i ? { ...r, [field]: value } : r)));
  const removeRow = (i: number) => setRows((p) => p.filter((_, j) => j !== i));

  const totalStudents = rows.reduce((s, r) => s + (parseInt(r.studentCount, 10) || 0), 0);
  const yearLevelsInvalid =
    rows.length === 0 || rows.some((r) => !r.yearLevelCode || !(parseInt(r.studentCount, 10) > 0));

  // A moment is complete once it has a day (and, for vakdocent, a vak). Incomplete draft rows
  // are simply skipped on save (so a half-filled row never blocks saving the group).
  const momentComplete = (m: MomentDraft, isVak: boolean) => !!m.day && (!isVak || !!m.reason.trim());
  // Only the "opgeven" choice shows an editor, so drafts under "geen"/"later" are ignored and
  // never count as incomplete.
  const anyMomentPartiallyFilled =
    (dagdeelChoice === "opgeven" && dagdelen.some((m) => !momentComplete(m, false))) ||
    (vakChoice === "opgeven" && vakdocenten.some((m) => !momentComplete(m, true)));

  const nameInvalid = !name.trim();
  // Per type the school must pick exactly one of the three choices. "geen"/"later" are always a
  // valid answer on their own; "opgeven" additionally needs at least one complete moment (a
  // partially-filled draft is skipped on save, so it does not count as an entry here).
  const dagdeelMissing = dagdeelChoice === "opgeven" && !dagdelen.some((m) => momentComplete(m, false));
  const vakMissing = vakChoice === "opgeven" && !vakdocenten.some((m) => momentComplete(m, true));
  const formValid = !nameInvalid && !yearLevelsInvalid && !dagdeelMissing && !vakMissing;

  // ── Save: group + leerjaren first, then this group's per-group constraints ──
  const handleSave = async () => {
    // The button stays enabled while invalid; a click reveals the errors for the failed
    // fields rather than silently doing nothing, then aborts without saving or closing.
    if (!formValid) { setSubmitted(true); focusFirstError(bodyRef.current); return; }
    if (submitting) return;
    setSubmitting(true);
    setFormError(null);
    onSaving();
    try {
      const displayName = uniqueGroupName(name.trim(), groupId);
      const groupCode = slug(displayName) || `groep-${Date.now()}`;
      const yls = rows.map((r) => ({ yearLevelCode: r.yearLevelCode, studentCount: Number(r.studentCount) }));

      // Map each 3-way choice to its two booleans. The server normalizes them (mutually
      // exclusive, _geen wins), but we already keep them clean here: exactly one is true for
      // "geen"/"later", both false for "opgeven".
      const availFlags = {
        vrijeDagdelenLater: dagdeelChoice === "later",
        vrijeDagdelenGeen: dagdeelChoice === "geen",
        vakdocentLater: vakChoice === "later",
        vakdocentGeen: vakChoice === "geen",
      };

      // 1) Create or update the group. The group MUST exist (and have a real id) before its
      //    constraints, since each constraint scopes to this group's id. On create the server
      //    also materializes the vaste defaults for the new group's leerjaren.
      let savedGroupId = groupId;
      if (savedGroupId == null) {
        const res = await fetch(`/api/school/${schoolId}/classes`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ groupCode, displayName, yearLevels: yls, ...availFlags }),
        });
        if (!res.ok) { setFormError("Opslaan is niet gelukt. Probeer het opnieuw."); onError(); setSubmitting(false); return; }
        const d = await res.json();
        savedGroupId = d.id as number;
      } else {
        const res = await fetch(`/api/school/${schoolId}/classes/${savedGroupId}`, {
          method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ displayName, groupCode, yearLevels: yls, ...availFlags }),
        });
        if (!res.ok) { setFormError("Opslaan is niet gelukt. Probeer het opnieuw."); onError(); setSubmitting(false); return; }
      }

      // 2) Per-group constraints. Mirrors VerhinderingenSection's per-type scoping:
      //    vrije dagdelen -> recurring_unavailable, appliesToEntireSchool=false, scope this group.
      //    vakdocent      -> vakdocent,             appliesToEntireSchool=false, scope this group.
      const scope = { classGroupIds: [savedGroupId] };

      const upsertMoment = async (m: MomentDraft, type: string, isVak: boolean): Promise<boolean> => {
        if (!momentComplete(m, isVak)) return true; // skip incomplete drafts
        const { startTime, endTime } = dayPartTimes(m.part);
        const title = isVak ? m.reason.trim() : AUTO_REASON_DAGDEEL;
        const weeklyPatterns = [{ dayOfWeekCode: m.day, startTime, endTime }];
        if (m.id == null) {
          const res = await fetch(`/api/school/${schoolId}/constraints`, {
            method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
            body: JSON.stringify({
              constraintTypeCode: type, constraintEffectCode: "blocked", title,
              appliesToEntireSchool: false, weeklyPatterns, scope,
            }),
          });
          return res.ok;
        }
        const res = await fetch(`/api/school/${schoolId}/constraints/${m.id}`, {
          method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ title, weeklyPatterns, scope }),
        });
        return res.ok;
      };

      // Only "opgeven" keeps entries. "geen"/"later" keep none: ignore that type's drafts and
      // remove anything it had, so a "geen"/"later" answer never coexists with concrete entries
      // for the same group + type.
      const dagdeelOpgeven = dagdeelChoice === "opgeven";
      const vakOpgeven = vakChoice === "opgeven";
      const keptDagdeelIds = dagdeelOpgeven ? new Set(dagdelen.map((m) => m.id).filter((x): x is number => x != null)) : new Set<number>();
      const keptVakIds = vakOpgeven ? new Set(vakdocenten.map((m) => m.id).filter((x): x is number => x != null)) : new Set<number>();
      const toDelete = [
        ...initialDagdeelIds.current.filter((id) => !keptDagdeelIds.has(id)),
        ...initialVakIds.current.filter((id) => !keptVakIds.has(id)),
      ];

      let allOk = true;
      if (dagdeelOpgeven) { for (const m of dagdelen) { if (!(await upsertMoment(m, "recurring_unavailable", false))) allOk = false; } }
      if (vakOpgeven) { for (const m of vakdocenten) { if (!(await upsertMoment(m, "vakdocent", true))) allOk = false; } }
      for (const id of toDelete) {
        const res = await fetch(`/api/school/${schoolId}/constraints/${id}`, { method: "DELETE", credentials: "include" });
        if (!res.ok) allOk = false;
      }

      if (!allOk) {
        // The group saved but a moment did not. Reload so the dialog/list reflect what stuck,
        // and surface the error rather than silently flashing "Opgeslagen".
        setFormError("De groep is opgeslagen, maar niet alle vrije dagdelen of vakdocenten konden worden bewaard. Probeer het opnieuw.");
        await onSaved();
        onError();
        setSubmitting(false);
        return;
      }

      await onSaved();
      setSubmitting(false);
      onClose();
    } catch {
      setFormError("Er ging iets mis bij het opslaan. Probeer het opnieuw.");
      onError();
      setSubmitting(false);
    }
  };

  // ── Delete the whole group ──
  const askDelete = async () => {
    setDeleteDeps(null);
    setDeleteConfirm(true);
    if (groupId == null) return;
    try {
      const res = await fetch(`/api/school/${schoolId}/classes/${groupId}/dependencies`, { credentials: "include" });
      if (res.ok) setDeleteDeps(await res.json());
    } catch { /* the confirm still works without the linked-items list */ }
  };

  const doDelete = async () => {
    if (groupId == null) return;
    setSubmitting(true);
    onSaving();
    try {
      const res = await fetch(`/api/school/${schoolId}/classes/${groupId}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) { onError(); setSubmitting(false); return; }
      await onSaved();
      setSubmitting(false);
      onClose();
    } catch { onError(); setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose(); }}>
      <DialogContent className="max-w-2xl" closeProps={{ disabled: submitting }}>
        <DialogHeader>
          <DialogTitle>{existing ? `${existing.displayName} bewerken` : "Groep toevoegen"}</DialogTitle>
          <DialogDescription>
            Geef aan welke leerjaren in de groep zitten en hoeveel leerlingen het zijn. U kunt hier ook vaste vrije dagdelen en vakdocenten van deze groep invullen.
          </DialogDescription>
        </DialogHeader>

        <DialogPanel className="space-y-6" ref={bodyRef}>
          {/* 1) Naam */}
          <div>
            <Label htmlFor="group-name" className="flex text-sm font-medium text-foreground">Naam van de groep<RequiredMark /></Label>
            <Input
              id="group-name"
              placeholder="bijv. Groep 3a"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5"
              aria-invalid={submitted && nameInvalid}
              autoFocus
            />
            {submitted && nameInvalid && <p className="mt-1 text-xs text-amber-700">Vul een naam in.</p>}
          </div>

          {/* 2) Leerjaren + aantallen */}
          <div>
            <h3 className="text-sm font-medium text-foreground">Leerjaren<RequiredMark /></h3>
            <p className="mt-0.5 text-sm leading-6 text-muted-foreground">Welke leerjaren zitten in deze groep, en hoeveel leerlingen?</p>
            <div className="mt-2.5 space-y-2">
              {rows.map((row, i) => (
                <div key={row.localId} className="grid grid-cols-[minmax(0,1fr)_6rem_auto] items-center gap-2 rounded-md border border-input bg-background p-2 shadow-xs">
                  <select
                    value={row.yearLevelCode}
                    onChange={(e) => updateRow(i, "yearLevelCode", e.target.value)}
                    aria-invalid={submitted && !row.yearLevelCode}
                    className={`h-9 rounded-md border border-input bg-background px-2.5 text-sm aria-invalid:border-red-500 ${row.yearLevelCode ? "" : "text-muted-foreground"}`}
                    aria-label="Leerjaar"
                  >
                    <option value="" disabled>Leerjaar toewijzen</option>
                    {YEAR_LEVELS.filter((y) => y.code === row.yearLevelCode || !rows.some((r) => r.yearLevelCode === y.code)).map((y) => (
                      <option key={y.code} value={y.code}>{y.label}</option>
                    ))}
                  </select>
                  <Input
                    type="number" min={1} inputMode="numeric"
                    value={row.studentCount}
                    onChange={(e) => updateRow(i, "studentCount", e.target.value)}
                    className="h-9 text-sm [&_[data-slot=input]]:h-full [&_[data-slot=input]]:leading-9"
                    aria-invalid={submitted && !(parseInt(row.studentCount, 10) > 0)}
                    aria-label="Aantal leerlingen" placeholder="Aantal"
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(i)} className="h-9 px-2 text-red-700 hover:text-red-700">
                    Verwijderen
                  </Button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addRow} className="mt-2">
              <PlusIcon size={16} className="-ms-0.5 opacity-60" /> Leerjaar toevoegen
            </Button>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                {rows.length} {rows.length === 1 ? "leerjaar" : "leerjaren"} · {totalStudents} leerlingen
              </span>
            </div>
            {submitted && yearLevelsInvalid && rows.length > 0 && (
              <p className="mt-1 text-xs text-amber-700">Kies per rij een leerjaar en vul het aantal leerlingen in.</p>
            )}
            {submitted && rows.length === 0 && (
              <p className="mt-1 text-xs text-amber-700">Voeg minimaal één leerjaar toe.</p>
            )}
          </div>

          {/* 3) Vrije dagdelen */}
          <div data-invalid={submitted && dagdeelMissing ? "true" : undefined}>
            <h3 className="text-sm font-medium text-foreground">Vaste vrije dagdelen<RequiredMark /></h3>
            <p className="mt-0.5 text-sm leading-6 text-muted-foreground">
              Is deze groep op een vast dagdeel niet op school? Bijvoorbeeld iedere woensdagmiddag vrij.
            </p>
            <AvailabilitySegmented
              name="dagdeel-keuze"
              value={dagdeelChoice}
              onChange={setDagdeelChoice}
              geenLabel="Geen vrije dagdelen"
            />
            {dagdeelChoice === "opgeven" ? (
              <>
                <MomentList
                  moments={dagdelen}
                  setMoments={setDagdelen}
                  isVak={false}
                  newDraft={() => ({ localId: newLocalId(), id: null, day: "", part: "ochtend", reason: AUTO_REASON_DAGDEEL })}
                  addLabel="Vrij dagdeel toevoegen"
                />
                {submitted && dagdeelMissing && (
                  <p className="mt-2 text-xs text-amber-700">Voeg een vrij dagdeel toe, of kies Geen, of kies Wordt later ingevuld.</p>
                )}
              </>
            ) : (
              <ChoiceHint choice={dagdeelChoice} geenText="Deze groep heeft geen vaste vrije dagdelen." />
            )}
          </div>

          {/* 4) Vakdocenten */}
          <div data-invalid={submitted && vakMissing ? "true" : undefined}>
            <h3 className="text-sm font-medium text-foreground">Vakdocenten<RequiredMark /></h3>
            <p className="mt-0.5 text-sm leading-6 text-muted-foreground">
              Heeft deze groep op een vast moment een vakdocent (gym, zwemmen, muziek...)? Dan houdt KUVO daar rekening mee.
            </p>
            <AvailabilitySegmented
              name="vak-keuze"
              value={vakChoice}
              onChange={setVakChoice}
              geenLabel="Geen vakdocenten"
            />
            {vakChoice === "opgeven" ? (
              <>
                <MomentList
                  moments={vakdocenten}
                  setMoments={setVakdocenten}
                  isVak={true}
                  newDraft={() => ({ localId: newLocalId(), id: null, day: "", part: "ochtend", reason: "" })}
                  addLabel="Vakdocent toevoegen"
                />
                {submitted && vakMissing && (
                  <p className="mt-2 text-xs text-amber-700">Voeg een vakdocent toe, of kies Geen, of kies Wordt later ingevuld.</p>
                )}
              </>
            ) : (
              <ChoiceHint choice={vakChoice} geenText="Deze groep heeft geen vakdocenten." />
            )}
          </div>

          {anyMomentPartiallyFilled && (
            <p className="flex items-start gap-1.5 text-xs leading-5 text-amber-700">
              <AlertTriangleIcon size={13} className="mt-0.5 shrink-0" />
              Een of meer dagdelen of vakdocenten zijn nog niet compleet. Vul ze aan, of verwijder ze; ze worden anders niet opgeslagen.
            </p>
          )}

          {formError && <p className="text-sm text-destructive">{formError}</p>}

          {/* Delete the whole group */}
          {existing && (
            <div className="border-t border-border pt-4">
              {deleteConfirm ? (
                <Notice
                  tone="error"
                  icon={<AlertCircleIcon />}
                  title={`Weet u zeker dat u ${existing.displayName} wilt verwijderen?`}
                  action={
                    <div className="flex items-center gap-3">
                      <Button size="sm" variant="outline" onClick={doDelete} disabled={submitting}>
                        Ja, verwijderen
                      </Button>
                      <button type="button" onClick={() => { setDeleteConfirm(false); setDeleteDeps(null); }} className="cursor-pointer text-sm font-medium text-primary hover:underline">
                        Annuleren
                      </button>
                    </div>
                  }
                >
                  {(() => {
                    const parts: string[] = [];
                    if (deleteDeps?.vrijeDagdelen) parts.push(`${deleteDeps.vrijeDagdelen} vrij${deleteDeps.vrijeDagdelen === 1 ? " dagdeel" : "e dagdelen"}`);
                    if (deleteDeps?.vakdocenten) parts.push(`${deleteDeps.vakdocenten} vakdocent-moment${deleteDeps.vakdocenten === 1 ? "" : "en"}`);
                    if (deleteDeps?.programmaKeuzes) parts.push(`${deleteDeps.programmaKeuzes} programmakeuze${deleteDeps.programmaKeuzes === 1 ? "" : "s"}`);
                    if (parts.length === 0) return "Dit kan niet ongedaan worden gemaakt.";
                    const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} en ${parts[parts.length - 1]}`;
                    return `Deze groep is gekoppeld aan ${list}. Als u de groep verwijdert, vervallen die ook. Dit kan niet ongedaan worden gemaakt.`;
                  })()}
                </Notice>
              ) : (
                <button type="button" onClick={askDelete} className="cursor-pointer text-sm font-medium text-red-700 hover:underline">
                  Groep verwijderen
                </button>
              )}
            </div>
          )}
        </DialogPanel>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Annuleren
          </Button>
          <Button onClick={handleSave} disabled={submitting}>
            {submitting ? "Opslaan..." : existing ? "Wijzigingen opslaan" : "Groep toevoegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MomentList — the day/dagdeel(/vak) rows for one moment type
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function MomentList({
  moments, setMoments, isVak, newDraft, addLabel,
}: {
  moments: MomentDraft[];
  setMoments: React.Dispatch<React.SetStateAction<MomentDraft[]>>;
  isVak: boolean;
  newDraft: () => MomentDraft;
  addLabel: string;
}) {
  const update = (localId: string, patch: Partial<MomentDraft>) =>
    setMoments((p) => p.map((m) => (m.localId === localId ? { ...m, ...patch } : m)));
  const remove = (localId: string) => setMoments((p) => p.filter((m) => m.localId !== localId));

  // Vakdocenten: which named vak-options to show as chips; "Anders" frees the text input.
  const namedVak = VAK_OPTIONS.filter((r) => r !== "Anders");

  return (
    <div className="mt-2.5 space-y-2.5">
      {moments.map((m) => {
        const isCustomVak = isVak && !namedVak.includes(m.reason);
        return (
          <div key={m.localId} className="rounded-md border border-input bg-background p-3 shadow-xs space-y-2.5">
            {/* Day chips */}
            <div>
              <Label className="text-xs text-muted-foreground">Dag</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d) => (
                  <ChipButton key={d.code} selected={m.day === d.code} onClick={() => update(m.localId, { day: d.code })}>
                    {d.label}
                  </ChipButton>
                ))}
              </div>
            </div>
            {/* Dagdeel chips */}
            <div>
              <Label className="text-xs text-muted-foreground">Dagdeel</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {DAY_PARTS.map((dp) => (
                  <ChipButton key={dp.code} selected={m.part === dp.code} onClick={() => update(m.localId, { part: dp.code })}>
                    {dp.label}
                  </ChipButton>
                ))}
              </div>
            </div>
            {/* Vak (vakdocenten only) */}
            {isVak && (
              <div>
                <Label className="text-xs text-muted-foreground">Vak</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {namedVak.map((r) => (
                    <ChipButton key={r} selected={m.reason === r} onClick={() => update(m.localId, { reason: r })}>{r}</ChipButton>
                  ))}
                  <ChipButton selected={isCustomVak} onClick={() => update(m.localId, { reason: "" })}>Anders</ChipButton>
                </div>
                {isCustomVak && (
                  <Input
                    placeholder="Vul het vak in"
                    value={m.reason}
                    onChange={(e) => update(m.localId, { reason: e.target.value })}
                    className="mt-1.5 h-9 max-w-xs text-sm"
                  />
                )}
              </div>
            )}
            <div className="flex justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => remove(m.localId)} className="h-8 px-2 text-red-700 hover:text-red-700">
                <TrashIcon size={14} className="-ms-0.5" /> Verwijderen
              </Button>
            </div>
          </div>
        );
      })}

      <Button type="button" variant="outline" size="sm" onClick={() => setMoments((p) => [...p, newDraft()])}>
        <PlusIcon size={16} className="-ms-0.5 opacity-60" /> {addLabel}
      </Button>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Availability choice — the 3-way "opgeven / geen / later" segmented control
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Three mutually-exclusive options for one per-group availability type. A radio group,
 *  styled as a segmented control so exactly one is always chosen. The "Geen ..." label is
 *  type-specific ("Geen vrije dagdelen" / "Geen vakdocenten"). */
function AvailabilitySegmented({
  name, value, onChange, geenLabel,
}: {
  name: string;
  value: AvailChoice;
  onChange: (v: AvailChoice) => void;
  geenLabel: string;
}) {
  const options: { value: AvailChoice; label: string }[] = [
    { value: "opgeven", label: "Opgeven" },
    { value: "geen", label: geenLabel },
    { value: "later", label: "Wordt later ingevuld" },
  ];
  return (
    <div role="radiogroup" className="mt-2.5 inline-flex flex-wrap gap-1.5 rounded-lg border border-input bg-muted/40 p-1 shadow-xs">
      {options.map((o) => {
        const selected = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            name={name}
            onClick={() => onChange(o.value)}
            className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              selected
                ? "bg-background text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {selected && <CheckIcon size={14} className="shrink-0 text-primary" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Calm confirmation shown in place of the entry editor when the type is "geen" or "later".
 *  "geen" reads as a normal muted confirmation; "later" gets the amber pending treatment. */
function ChoiceHint({ choice, geenText }: { choice: AvailChoice; geenText: string }) {
  if (choice === "later") {
    return (
      <div className="mt-2.5 rounded-md border border-amber-200 bg-amber-50/60 p-3">
        <p className="flex items-center gap-1.5 text-sm font-medium text-amber-900">
          <ClockIcon size={14} className="shrink-0 opacity-70" /> Wordt later ingevuld
        </p>
        <p className="mt-0.5 text-sm leading-5 text-amber-800">
          U kunt dit later nog invullen; KUVO houdt er rekening mee. Kies Opgeven zodra u het wilt invullen.
        </p>
      </div>
    );
  }
  return (
    <p className="mt-2.5 text-sm leading-6 text-muted-foreground">{geenText}</p>
  );
}

/** A small "verplicht" marker shown after a required section label. */
function RequiredMark() {
  return (
    <span aria-hidden="true" className="ms-0.5 text-red-600" title="Verplicht">
      *
    </span>
  );
}

/** A tactile selectable chip (toggle-style button). */
function ChipButton({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        selected
          ? "border-primary bg-primary/5 text-primary"
          : "border-input bg-background text-foreground shadow-xs hover:bg-accent"
      }`}
    >
      {selected && <CheckIcon size={14} className="shrink-0" />}
      {children}
    </button>
  );
}
